/**
 * Компонент Lightbox для просмотра изображений
 */

import { useEffect } from 'react';
import { X, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useConfigStore } from '@/store/configStore';
import styles from './Lightbox.module.css';
import { chatLogger } from '@/utils/chatLogger';

/**
 * Пропсы компонента Lightbox
 */
interface LightboxProps {
  /** URL изображения */
  imageUrl: string;
  /** Название файла (опционально) */
  filename?: string;
  /** Колбек закрытия */
  onClose: () => void;
  /** Предыдущее изображение (опционально) */
  onPrevious?: () => void;
  /** Следующее изображение (опционально) */
  onNext?: () => void;
}

/**
 * Lightbox для просмотра изображений в полный размер
 * 
 * @description Модальное окно с изображением, кнопками управления и navigation
 */
export function Lightbox({
  imageUrl,
  filename,
  onClose,
  onPrevious,
  onNext,
}: LightboxProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'ArrowLeft' && onPrevious) {
        onPrevious();
      } else if (e.key === 'ArrowRight' && onNext) {
        onNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrevious, onNext]);

  useScrollLock(true);

  const t = useConfigStore((s) => s.translations);
  const handleDownload = async () => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'image.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      chatLogger.error('[Lightbox] Ошибка скачивания:', error);
    }
  };

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t.aria_view_image || 'Image viewer'}
    >
      <div className={styles.content} onClick={(e) => e.stopPropagation()}>
        {/* Кнопки управления */}
        <div className={styles.controls}>
          <button
            className={styles.controlBtn}
            onClick={handleDownload}
            aria-label={t.aria_download || 'Download'}
            type="button"
          >
            <Download size={20} strokeWidth={2} />
          </button>
          
          <button
            className={styles.controlBtn}
            onClick={onClose}
            aria-label={t.close || 'Close'}
            type="button"
          >
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        {/* Изображение */}
        <img src={imageUrl} alt={filename || t.image_alt || 'Image'} className={styles.image} />

        {/* Название файла */}
        {filename && <div className={styles.filename}>{filename}</div>}

        {/* Navigation кнопки */}
        {onPrevious && (
          <button
            className={`${styles.navBtn} ${styles.prev}`}
            onClick={onPrevious}
            aria-label={t.aria_prev_image || 'Previous image'}
            type="button"
          >
            <ChevronLeft size={28} strokeWidth={2} />
          </button>
        )}

        {onNext && (
          <button
            className={`${styles.navBtn} ${styles.next}`}
            onClick={onNext}
            aria-label={t.aria_next_image || 'Next image'}
            type="button"
          >
            <ChevronRight size={28} strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  );
}

